import type { EquipmentLabel } from 'shared';
import {
  GRID_LEFT,
  GRID_TOP,
  NAME_LINE_HEIGHT,
  QUIET_ZONE,
  SHEET,
  cutLines,
  nameSize,
  stickerEdges,
} from '@/lib/labelSheet';

const mm = (value: number) => `${value}mm`;

const CUT = '0.2mm dashed #737373';
const MARK = '0.15mm solid #595959';

interface LabelSheetProps {
  labels: EquipmentLabel[];
  /** 1-based, for the sheet's accessible name. */
  page: number;
  pages: number;
}

/**
 * One A4 sheet of stickers, drawn in millimetres so that it prints at its true
 * size. Each sticker: the DIGGER logo, the equipment's code, its name.
 */
export default function LabelSheet({ labels, page, pages }: LabelSheetProps) {
  const cuts = cutLines(labels.length);

  return (
    <section
      className="label-sheet relative bg-white text-black"
      style={{ width: mm(SHEET.pageWidth), height: mm(SHEET.pageHeight) }}
      aria-label={`Folha ${page} de ${pages}`}
    >
      <div
        className="absolute"
        style={{
          left: mm(GRID_LEFT),
          top: mm(GRID_TOP),
          width: mm(cuts.width),
          height: mm(cuts.height),
        }}
      >
        {labels.map((label, index) => {
          const { drawRight, drawBottom } = stickerEdges(index, labels.length);
          return (
            <div
              // An equipment may be on the sheet more than once.
              key={`${label.id}-${index}`}
              className="absolute box-border flex flex-col items-center"
              style={{
                left: mm((index % SHEET.columns) * SHEET.stickerWidth),
                top: mm(Math.floor(index / SHEET.columns) * SHEET.stickerHeight),
                width: mm(SHEET.stickerWidth),
                height: mm(SHEET.stickerHeight),
                padding: mm(SHEET.padding),
                borderLeft: CUT,
                borderTop: CUT,
                borderRight: drawRight ? CUT : undefined,
                borderBottom: drawBottom ? CUT : undefined,
              }}
            >
              <img
                src="/digger-logo.png"
                alt=""
                style={{ width: mm(SHEET.logo), height: mm(SHEET.logo) }}
              />
              <img
                src={label.qrCode}
                alt={`Código QR de ${label.name}`}
                style={{
                  width: mm(SHEET.code),
                  height: mm(SHEET.code),
                  marginTop: mm(QUIET_ZONE),
                  marginBottom: mm(QUIET_ZONE),
                  imageRendering: 'pixelated',
                }}
              />
              <p
                className="flex flex-1 items-center text-center font-bold"
                style={{
                  fontFamily: 'Helvetica, Arial, sans-serif',
                  fontSize: `${nameSize(label.name)}pt`,
                  lineHeight: NAME_LINE_HEIGHT,
                  overflowWrap: 'anywhere',
                }}
              >
                {label.name}
              </p>
            </div>
          );
        })}

        {/* Marks in the margin, in line with every cut. */}
        {cuts.vertical.map((x) => (
          <span key={`v${x}`} aria-hidden="true">
            <span
              className="absolute"
              style={{
                left: mm(x),
                top: mm(-SHEET.markGap - SHEET.markLength),
                height: mm(SHEET.markLength),
                borderLeft: MARK,
              }}
            />
            <span
              className="absolute"
              style={{
                left: mm(x),
                top: mm(cuts.height + SHEET.markGap),
                height: mm(SHEET.markLength),
                borderLeft: MARK,
              }}
            />
          </span>
        ))}
        {cuts.horizontal.map((y) => (
          <span key={`h${y}`} aria-hidden="true">
            <span
              className="absolute"
              style={{
                top: mm(y),
                left: mm(-SHEET.markGap - SHEET.markLength),
                width: mm(SHEET.markLength),
                borderTop: MARK,
              }}
            />
            <span
              className="absolute"
              style={{
                top: mm(y),
                left: mm(cuts.width + SHEET.markGap),
                width: mm(SHEET.markLength),
                borderTop: MARK,
              }}
            />
          </span>
        ))}
      </div>
    </section>
  );
}
